import URI from "urijs";

export interface IJsonObject {
	[key: string]: any;
}

type Fetch = (path: RequestInfo, init?: RequestInit) => Promise<Response>;

export class ApiService {
	private fetch: Fetch;

	constructor(fetch?: Fetch) {
		if (!fetch) {
			this.fetch = window.fetch.bind(window);
		} else {
			this.fetch = fetch;
		}
	}

	public async get(path: string, params?: object): Promise<Response> {
		const uri = new URI(path);
		if (params) {
			uri.setSearch(params);
		}

		return this.fetch(uri.toString(), params);
	}

	public async post(path: string, body?: object): Promise<Response> {
		let headers;

		if (body) {
			headers = {
				"Content-Type": "application/json",
			};
		}

		return this.fetch(path, {
			method: "post",
			body: body ? JSON.stringify(body) : undefined,
			headers,
		});
	}

	/**
	 * Sends only the fields given, asking for a JSON answer so a failed
	 * validation comes back as a 422 rather than a redirect.
	 */
	public async patch(path: string, body: object): Promise<Response> {
		return this.fetch(path, {
			method: "PATCH",
			body: JSON.stringify(body),
			headers: {
				Accept: "application/json",
				"Content-Type": "application/json",
			},
		});
	}

	/**
	 * Reads a JSON response, throwing with the server's message when it failed.
	 */
	public static async readJson(response: Response): Promise<any> {
		const body = await response.json().catch(() => null);

		if (!response.ok) {
			throw new Error(ApiService.errorMessage(body, response.status));
		}

		return body;
	}

	private static errorMessage(body: any, status: number): string {
		const error = body && body.error;
		const fieldErrors =
			error && error.errors && Object.values(error.errors);
		if (fieldErrors && fieldErrors.length) {
			return ([] as string[]).concat(...(fieldErrors as string[][]))[0];
		}

		if (status === 403) {
			return "You don't have permission to do that.";
		}

		return (
			(error && error.message) ||
			(body && body.message) ||
			`The server answered ${status}.`
		);
	}

	public async delete(path: string): Promise<Response> {
		return this.fetch(path, {
			method: "DELETE",
		});
	}

	public async uploadFile(path: string, body: FormData): Promise<Response> {
		return this.fetch(path, {
			method: "post",
			body,
		});
	}
}
